import clsx from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {useEffect} from 'react';
import {useNavigationStore} from '../../../hooks/useNavigationStore';
import {LocalizedSlugs, SanityNavigation} from '../../../types';
import translate from '../../../utils/getTranslation';
import CartToggle from '../../cart/CartToggle.client';
import LanguageSelector from '../LanguageSelector.client';
import NavigationLink from './NavigationLink';
import NavigationMain from './NavigationMain.client';
import NavigationSecondary from './NavigationSecondary.client';

type Props = {
  navigation: SanityNavigation;
  slugs?: LocalizedSlugs;
};

export default function Navigation({navigation, slugs}: Props) {
  const {isOpen, handleClose} = useNavigationStore();

  useEffect(() => {
    {
      isOpen
        ? document.documentElement.classList.add('overflow-hidden')
        : document.documentElement.classList.remove('overflow-hidden');
    }
  }, [isOpen]);

  const TRANSITIONS = {
    DURATION: 0.4,
    EASE: [0.25, 0.1, 0.25, 1.0],
  };

  const VARIANTS = {
    open: {
      x: 0,
      originY: 0,
      opacity: 1,
      transition: {
        duration: TRANSITIONS.DURATION,
        ease: TRANSITIONS.EASE,
      },
    },
    closed: {
      x: `-100%`,
      transition: {
        duration: TRANSITIONS.DURATION,
        ease: TRANSITIONS.EASE,
      },
    },
  };

  const primaryNavigation = navigation.primaryNavigation;
  const CLOSE = translate('CLOSE');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.nav className="fixed inset-0 z-[90]">
          <Background />
          <motion.div
            variants={VARIANTS}
            initial="closed"
            animate="open"
            exit="closed"
            className="h-screen-safe fixed left-0 flex w-full flex-col gap-y-8 bg-neutral-100 text-neutral-900 md:w-1/2"
          >
            <div className="sticky top-0 flex w-full justify-between gap-6 bg-neutral-100 py-5 px-4 text-sm md:px-8">
              <div className="flex gap-6">
                <button onClick={handleClose} className="hover:underline">
                  {CLOSE}
                </button>
                <nav className={clsx('hidden gap-6 text-sm md:flex')}>
                  {primaryNavigation?.links &&
                    primaryNavigation?.links.map((link) => (
                      <NavigationLink key={link._key} link={link} />
                    ))}
                </nav>
                {/* Language selector */}
              </div>
              <div className="flex gap-6 md:hidden">
                <LanguageSelector slugs={slugs} />
                <CartToggle onClick={handleClose} className="md:hidden" />
              </div>
            </div>

            <NavigationMain {...navigation} />
            <NavigationSecondary {...navigation} />
          </motion.div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}

function Background() {
  const {toggleOpen} = useNavigationStore();
  return (
    <motion.div
      onClick={toggleOpen}
      initial={{opacity: 0}}
      animate={{opacity: 0.2}}
      exit={{opacity: 0}}
      className={clsx('fixed inset-0 h-screen w-screen', 'bg-neutral-900')}
    ></motion.div>
  );
}
